import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart, CreditCard, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { supporterFeeCents, money } from "@/lib/supporterFee";

const PRESET_TIPS = [500, 1000, 2500, 5000];

// =====================================================================
// TipModal — appears on the public Q&A page when a fan hits the tip button
// or the friendly-wall CTA. Routes through /api/tips/checkout, which decides
// whether to do a destination charge (Connect-ready creator) or platform-held
// credit (pre-Connect creator) — fan never sees the difference.
// =====================================================================
export default function TipModal({ open, slug, creatorName, onClose }) {
  const [amount, setAmount] = useState(500);
  const [custom, setCustom] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [bounds, setBounds] = useState({ min: 500, max: 10000000 });
  // Sep 25, 2026 — Supporter Service Fee (Model A). Dials come from
  // /s/{slug}; the fee is recomputed authoritatively at checkout. `applies` is
  // false when the creator has no payout account yet — a held tip earns the
  // platform nothing, so no fee is charged on it.
  const [feeCfg, setFeeCfg] = useState({ revenue_bps: 500, processing_bps: 290, fixed_cents: 30, applies: true });

  // Pull the configured min/max once when opened so the slider matches
  // whatever Super Admin / creator have set in their thresholds panel.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get(`/s/${slug}`);
        if (r.data?.tip_min_cents) setBounds({ min: r.data.tip_min_cents, max: r.data.tip_max_cents || 10000000 });
        setFeeCfg({
          revenue_bps: r.data?.supporter_fee_revenue_bps ?? 500,
          processing_bps: r.data?.supporter_fee_processing_bps ?? 290,
          fixed_cents: r.data?.supporter_fee_fixed_cents ?? 30,
          applies: r.data?.supporter_fee_applies_to_tips !== false,
        });
      } catch { /* fallthrough — defaults are fine */ }
    })();
  }, [open, slug]);

  if (!open) return null;

  const tipCents = custom ? Math.round(parseFloat(custom) * 100) : amount;
  const validTip = Number.isFinite(tipCents) && tipCents > 0;
  const feeCents = (validTip && feeCfg.applies) ? supporterFeeCents(tipCents, feeCfg) : 0;
  const totalCents = (validTip ? tipCents : 0) + feeCents;

  const submit = async (paymentMethod = null) => {
    const cents = custom ? Math.round(parseFloat(custom) * 100) : amount;
    if (!Number.isFinite(cents) || cents < bounds.min || cents > bounds.max) {
      return toast.error(`Tip must be between $${bounds.min/100} and $${bounds.max/100}.`);
    }
    setBusy(paymentMethod || "card");
    try {
      const r = await api.post("/tips/checkout", {
        slug, amount_cents: cents, note: note.trim() || undefined,
        origin_url: window.location.origin,
        // Sep 24, 2026 — "amazon_pay" pins the session to Amazon Pay's in-form
        // redirect flow. Left to Stripe's automatic list, Amazon Pay renders as
        // a popup wallet button that silently strands payers on Safari/iOS with
        // no charge and no error.
        payment_method: paymentMethod || undefined,
      });
      // Branch: regardless of connect_ready, we redirect to Stripe Checkout. The
      // backend webhook records the tip with the right status ('held' or 'paid_via_connect').
      if (r.data?.url) window.location.href = r.data.url;
      else toast.error("Could not start tip checkout.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tip failed.");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} data-testid="tip-modal"
      >
        <motion.div
          className="modal-safe bg-white rounded-xl border hairline shadow-2xl w-full max-w-md p-6"
          initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-3">
            <h3 className="font-serif-display text-2xl text-[#1C1B1A] flex items-center gap-2">
              <Heart size={22} weight="duotone" className="text-[#C16E5A]" />
              Tip {creatorName || "the creator"}
            </h3>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[#F0F1EE]" data-testid="tip-close">
              <X size={16} />
            </button>
          </div>
          <p className="text-xs text-[#6B6A68] mb-4">
            100% of your tip goes to the creator.
            {feeCfg.applies
              ? " The service fee covers My Silo's platform costs and payment processing."
              : " Even if they haven't activated payouts yet, your tip is safely held as a credit on their account."}
          </p>

          {/* Preset chips */}
          <div className="grid grid-cols-4 gap-2 mb-3" data-testid="tip-presets">
            {PRESET_TIPS.map((c) => (
              <button key={c} onClick={() => { setAmount(c); setCustom(""); }}
                data-testid={`tip-preset-${c}`}
                className={`py-2 rounded-md border text-sm font-medium ${(!custom && amount === c) ? "border-[#C16E5A] bg-[#FDF1ED] text-[#C16E5A]" : "hairline bg-white text-[#1C1B1A] hover:border-[#C16E5A]"}`}
              >${c/100}</button>
            ))}
          </div>

          {/* Custom amount */}
          <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Custom amount</label>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[#6B6A68]">$</span>
            <input type="number" min={bounds.min/100} max={bounds.max/100} step="1"
              value={custom} onChange={(e) => setCustom(e.target.value)}
              placeholder="Enter amount" data-testid="tip-custom"
              className="flex-1 bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#C16E5A]" />
          </div>

          <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Note (optional)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} rows={2}
            placeholder="Say something nice…" data-testid="tip-note"
            className="w-full bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#C16E5A] mb-4" />

          {validTip && (
            <div className="rounded-lg border hairline bg-[#FDFCF8] px-4 py-3 text-sm mb-4"
              data-testid="tip-price-block">
              <div className="flex items-center justify-between py-0.5">
                <span className="text-[#6B6A68]">Creator tip</span>
                <span className="text-[#1C1B1A] font-mono tabular-nums"
                  data-testid="tip-price-creator">{money(tipCents)}</span>
              </div>
              {feeCents > 0 && (
                <div className="flex items-center justify-between py-0.5">
                  <span className="text-[#6B6A68] flex items-center gap-1.5">
                    Supporter service fee
                    <span title="Covers My Silo's platform costs and payment processing."
                      data-testid="tip-fee-info"
                      className="w-4 h-4 rounded-full border hairline text-[10px] leading-none flex items-center justify-center text-[#6B6A68] cursor-help">
                      i
                    </span>
                  </span>
                  <span className="text-[#1C1B1A] font-mono tabular-nums"
                    data-testid="tip-price-fee">{money(feeCents)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 mt-1 border-t hairline">
                <span className="text-[#1C1B1A] font-medium">Total</span>
                <span className="text-[#1C1B1A] font-medium font-mono tabular-nums"
                  data-testid="tip-price-total">{money(totalCents)}</span>
              </div>
            </div>
          )}

          <button onClick={() => submit()} disabled={!!busy} data-testid="tip-submit"
            className="w-full text-sm py-3 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] disabled:opacity-60 flex items-center justify-center gap-1.5 transition-colors">
            <CreditCard size={14} weight="duotone" />
            {busy === "card" ? "Loading Stripe…" : `Tip ${money(totalCents)}`}
          </button>

          {/* Sep 24, 2026 — Amazon Pay gets its own route because Stripe only
              renders it reliably (as a redirect, not a popup) when the session
              asks for it alone. It is deliberately quiet: a secondary option,
              not a competing call to action. */}
          <button onClick={() => submit("amazon_pay")} disabled={!!busy} data-testid="tip-submit-amazon"
            className="w-full text-xs py-2.5 mt-2 rounded-md border hairline bg-transparent text-[#3A3936] hover:bg-[#F3F0E9] disabled:opacity-60 transition-colors">
            {busy === "amazon_pay" ? "Opening Amazon Pay…" : "Or pay with Amazon Pay"}
          </button>

          <p className="text-[10px] text-[#6B6A68] mt-2 text-center leading-relaxed">
            Amazon Pay isn't shown inside the card checkout — use the option above.
          </p>
          <p className="text-[10px] text-[#6B6A68] mt-1.5 text-center flex items-center justify-center gap-1">
            <ArrowSquareOut size={9} /> Secure checkout via Stripe
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
